import React, { useState } from 'react';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { formatPrice, formatDate, getStatusColor, getStatusText } from '../lib/utils';
import { toast } from 'sonner';
import { Search, Package, Clock, CheckCircle2, XCircle } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function TrackOrder() {
  const [orderNumber, setOrderNumber] = useState('');
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(false);
  const [searched, setSearched] = useState(false);

  const handleSearch = async (e) => {
    e.preventDefault();
    if (!orderNumber.trim()) {
      toast.error('Masukkan nomor order');
      return;
    }

    setLoading(true);
    setSearched(true);
    try {
      const response = await axios.get(`${API_URL}/api/orders/track/${orderNumber.trim()}`);
      setOrder(response.data.order);
    } catch (error) {
      console.error('Order not found:', error);
      setOrder(null);
      toast.error('Order tidak ditemukan');
    } finally {
      setLoading(false);
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'pending':
        return <Clock className="w-6 h-6 text-yellow-400" />;
      case 'processing':
        return <Package className="w-6 h-6 text-blue-400" />;
      case 'completed':
        return <CheckCircle2 className="w-6 h-6 text-green-400" />;
      case 'failed':
        return <XCircle className="w-6 h-6 text-red-400" />;
      default:
        return null;
    }
  };

  return (
    <div className="min-h-screen pt-24 pb-12 cyber-grid">
      <div className="max-w-2xl mx-auto px-4">
        {/* HUD Header */}
        <div className="text-center mb-8">
          <div className="inline-flex items-center gap-2 mb-3 px-3 py-1 rounded-full border border-primary/30 bg-primary/5">
            <span className="w-1.5 h-1.5 rounded-full bg-primary animate-pulse" />
            <span className="font-mono text-[10px] text-primary tracking-widest uppercase">TRANSACTION_TRACKER · SCAN</span>
          </div>
          <h1 className="font-bold text-3xl text-white uppercase mb-2 tracking-wider">
            Cek Transaksi
          </h1>
          <p className="text-muted-foreground font-mono text-sm">
            Masukkan nomor order untuk scan status transmisi
          </p>
        </div>

        {/* Search terminal card */}
        <form onSubmit={handleSearch} className="bg-card/80 backdrop-blur-sm rounded-xl p-6 border border-border cyber-card mb-8 relative overflow-hidden">
          <div className="absolute top-0 left-0 w-6 h-6 border-t-2 border-l-2 border-primary/50 rounded-tl-xl" />
          <div className="absolute bottom-0 right-0 w-6 h-6 border-b-2 border-r-2 border-secondary/50 rounded-br-xl" />
          <div className="space-y-4">
            <div className="space-y-2">
              <label htmlFor="orderNumber" className="font-mono text-xs text-muted-foreground uppercase tracking-widest">▸ Nomor Order</label>
              <div className="flex gap-3">
                <Input
                  id="orderNumber" type="text"
                  placeholder="AFX20260112..."
                  className="bg-black/50 border-border text-white font-mono flex-1 focus:border-primary focus:ring-1 focus:ring-primary/30"
                  value={orderNumber}
                  onChange={(e) => setOrderNumber(e.target.value)}
                  data-testid="track-order-input"
                />
                <Button type="submit"
                  className="bg-primary hover:bg-primary/80 text-black font-bold neon-green"
                  disabled={loading} data-testid="track-order-btn">
                  {loading
                    ? <div className="animate-spin w-4 h-4 border-2 border-black border-t-transparent rounded-full" />
                    : <Search className="w-4 h-4" />}
                </Button>
              </div>
            </div>
          </div>
        </form>

        {/* Result */}
        {searched && !loading && (
          order ? (
            <div className="bg-card/80 backdrop-blur-sm rounded-xl border border-border cyber-card overflow-hidden">
              {/* Status Header */}
              <div className="p-6 border-b border-border bg-black/30">
                <div className="flex items-center gap-4">
                  {getStatusIcon(order.status)}
                  <div>
                    <p className="font-mono text-xs text-muted-foreground uppercase tracking-widest">Status</p>
                    <p className={`font-bold text-lg uppercase tracking-wider ${
                      order.status === 'completed' ? 'text-primary' :
                      order.status === 'pending' ? 'text-yellow-400' :
                      order.status === 'processing' ? 'text-blue-400' : 'text-red-400'
                    }`}>
                      {getStatusText(order.status)}
                    </p>
                  </div>
                  <div className="ml-auto font-mono text-xs text-muted-foreground">{order.order_number}</div>
                </div>
              </div>

              {/* Details */}
              <div className="p-6 space-y-3">
                {[
                  { label: 'Order Number', val: order.order_number, mono: true },
                  { label: 'Tanggal Order', val: formatDate(order.created_at) },
                  { label: 'Produk', val: order.product_name },
                  { label: 'Item', val: order.denomination_name },
                  { label: 'User ID', val: order.game_user_id, mono: true },
                  ...(order.game_server_id ? [{ label: 'Server ID', val: order.game_server_id, mono: true }] : []),
                ].map(row => (
                  <div key={row.label} className="flex justify-between items-start py-1 border-b border-border/40">
                    <span className="font-mono text-xs text-muted-foreground uppercase tracking-wider">{row.label}</span>
                    <span className={`text-sm text-white ${row.mono ? 'font-mono' : ''}`}>{row.val}</span>
                  </div>
                ))}
                <div className="flex justify-between items-center pt-2">
                  <span className="font-mono text-xs text-muted-foreground uppercase tracking-wider">Total</span>
                  <span className="font-mono text-xl font-bold text-primary text-glow-green">{formatPrice(order.price)}</span>
                </div>
              </div>
            </div>
          ) : (
            <div className="bg-card/80 rounded-xl p-8 border border-border cyber-card text-center">
              <XCircle className="w-12 h-12 text-secondary mx-auto mb-4" />
              <p className="text-white font-bold font-mono mb-2 uppercase tracking-wider">[ ORDER_NOT_FOUND ]</p>
              <p className="text-muted-foreground text-sm font-mono">Nomor order tidak terdeteksi dalam database orbital</p>
            </div>
          )
        )}
      </div>
    </div>
  );
}
